import { UniverseImagePipe } from '@/domain/universes/pipes/universe-image.pipe';
import { SenAnalysisFeatureService } from '@/features/sen-analysis/sen-analysis-feature.service';
import { SharedUiColorName } from '@/shared/ui/theming';
import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import { UiSectionContentComponent } from '@/ui/section/section-content/ui-section-content';
import { UiSectionComponent } from '@/ui/section/ui-section';
import { ReferenceListLabelPipe } from '@/utils/reference-lists/pipes/reference-list-label.pipe';
import { ReferenceListName } from '@/utils/reference-lists/reference-lists.interface';
import { ReferenceListsService } from '@/utils/reference-lists/reference-lists.service';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, type OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import type { Observable } from 'rxjs';
import type { Sensor } from '../../../interfaces/Sensor.interface';
import type { MultiTileData, MultiTileSensor } from '../../../interfaces/Tile.interface';
import { SharedUiColoredTextColorDirective } from '../../../libs/shared/ui/theming/lib/colors/shared-ui-color.directives';
import { LocalNumberPipe } from '../../../pipes/localnumber.pipe';
import { SensorPipe } from '../../../pipes/sensor.pipe';
import { SensorsProvider } from '../../../providers/sensors/sensors.provider';
import { generateSensorIdentifier } from '../../../providers/sensors/sensors.utils';
import { TilesProvider } from '../../../providers/tiles/tiles.provider';
import { AdvizeoModalService } from '../../../services/advizeo-modal.service';
import { FlatGaugeComponent, type Marker } from '../../flat-gauge/flat-gauge';
import { EditTextComponent } from '../../form-controls/controls/edit-text/edit-text';
import { LoadingAndErrorComponent } from '../../loading-and-error/loading-and-error';
import { SpawnableTile } from '../spawnable-tile/spawnable-tile.component';

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./tile-multi-sensors.scss'],
    selector: 'tile-multi-sensors',
    templateUrl: 'tile-multi-sensors.html',
    imports: [
        UiSectionComponent,
        UiSectionContentComponent,
        EditTextComponent,
        FormsModule,
        UiIconButtonComponent,
        SharedUiColoredTextColorDirective,
        FlatGaugeComponent,
        LoadingAndErrorComponent,
        AsyncPipe,
        SensorPipe,
        UniverseImagePipe,
        LocalNumberPipe,
        ReferenceListLabelPipe
    ]
})
export class TileMultiSensorsComponent extends SpawnableTile<MultiTileData> implements OnInit {
    private _tilesProvider = inject(TilesProvider);
    private _advizeoModalService = inject(AdvizeoModalService);
    private _translateService = inject(TranslateService);
    private _referenceListsService = inject(ReferenceListsService);
    private _sensorsProvider = inject(SensorsProvider);
    private _senAnalysisService = inject(SenAnalysisFeatureService);

    private sensorsValues: Map<string, number> = new Map<string, number>();

    @Input() tileData!: MultiTileData;

    areSensorsLoading$!: Observable<boolean>;

    ngOnInit() {
        this.areSensorsLoading$ = this._sensorsProvider.areSensorsLoading$();
    }

    getMarkers(sensorDesc: any, sensor: Sensor) {
        const markers: Marker[] = [];
        markers.push({
            value: sensorDesc.min,
            label: sensorDesc.min,
            labelVerticalOffset: 100,
            markerType: 'step'
        });
        markers.push({
            value: sensorDesc.max,
            label: sensorDesc.max,
            labelVerticalOffset: 100,
            markerType: 'step'
        });

        const value = this.getSensorValue(sensorDesc, sensor)!;
        let markerType;
        if (value >= sensorDesc.min && value <= sensorDesc.max) {
            markerType = 'pin-green';
        } else {
            markerType = 'pin-orange';
        }
        markers.push({
            value,
            markerType
        });
        return markers;
    }

    getTrackMin(sensorDesc: any) {
        return sensorDesc.trackMin || sensorDesc.trackMin === 0
            ? sensorDesc.trackMin
            : sensorDesc.min - 1.5 * (sensorDesc.max - sensorDesc.min);
    }

    getTrackMax(sensorDesc: any) {
        return sensorDesc.trackMax || sensorDesc.trackMax === 0
            ? sensorDesc.trackMax
            : sensorDesc.max + 1.5 * (sensorDesc.max - sensorDesc.min);
    }

    getSensorValue(sensorDesc: MultiTileSensor<void>, sensor: Sensor) {
        const sensorKey = generateSensorIdentifier(sensorDesc.sensorIdentifier.systemId, sensorDesc.sensorIdentifier.sensorId);
        let sensorValue = this.sensorsValues.get(sensorKey);

        if (!this.sensorsValues.has(sensorKey)) {
            const sensorIdentifier = sensorDesc.sensorIdentifier;
            const values = this.tileData.value;
            const sensorValueIdentifier =
                values && Array.isArray(values)
                    ? values.find(
                          value =>
                              value.sensorIdentifier.sensorId === sensorIdentifier.sensorId &&
                              value.sensorIdentifier.systemId === sensorIdentifier.systemId
                      )
                    : null;
            if (sensorValueIdentifier && sensor) {
                sensorValue = Math.round(sensorValueIdentifier.value);
                // Value for extensive sensor is only valid if not more than 30 days old
                if (
                    this._sensorsProvider.isSensorExtensive(sensor) &&
                    moment(sensorValueIdentifier.timestamp).isBefore(moment().subtract(30, 'days'))
                ) {
                    sensorValue = null!;
                }
                // Value for intensive sensor is only valid if not more than a day old
                if (
                    !this._sensorsProvider.isSensorExtensive(sensor) &&
                    moment(sensorValueIdentifier.timestamp).isBefore(moment().subtract(1, 'days'))
                ) {
                    sensorValue = null!;
                }
                this.sensorsValues.set(sensorKey, sensorValue);
            }
        }

        return sensorValue;
    }

    getValueColor(sensorDesc: MultiTileSensor<void>, sensor: Sensor): SharedUiColorName {
        const value = this.getSensorValue(sensorDesc, sensor)!;
        if (value >= sensorDesc.min && value <= sensorDesc.max) {
            return SharedUiColorName.SUCCESS;
        } else {
            return SharedUiColorName.FAILURE;
        }
    }

    getSensorUnitCode(sensor: Sensor): string {
        return this._referenceListsService.getReferenceListItemLabelR(ReferenceListName.UNIT, sensor.displayedUnitMeasureCode);
    }

    deleteTile(ev: MouseEvent) {
        ev.preventDefault();
        ev.stopPropagation();

        this._advizeoModalService.openLegacyDialog({
            title: this._translateService.instant('WIDGET.HOME_TILES.DELETE_CONFIRMATION_TITLE'),
            message: this._translateService.instant('WIDGET.HOME_TILES.DELETE_CONFIRMATION_MESSAGE', { tileName: this.tileData.name }),
            buttons: [
                {
                    text: this._translateService.instant('BUTTONS.CANCEL'),
                    role: 'cancel'
                },
                {
                    text: this._translateService.instant('BUTTONS.DELETE'),
                    handler: () => {
                        this.processDeleteTile();
                    }
                }
            ]
        });
    }

    private processDeleteTile() {
        this._tilesProvider.deleteTile(this.tileData.tileId, this.tileData.buildingId).then(() => {
            this.deleted.emit();
        });
    }

    updateTileName() {
        this._tilesProvider.renameTile(this.tileData.tileId, this.tileData.name);
    }

    async goToNext(tile: MultiTileData) {
        if (tile) {
            this._senAnalysisService.goToAnalysis(
                tile.buildingId,
                tile.description.sensors.map(sensor => sensor.timeseriesId)
            );
        }
    }
}
